import { api } from '../api';
import type { User, CreateUserInput, UpdateUserInput } from '@/types/user';
import type { Location } from '@/types/location';
import type { PaginationParams, PaginatedResponse } from '@/types/common';
import type { DashboardUsersStats } from '@/types/company';

export interface UserRoleAssignment {
  id: number;
  role: {
    id: number;
    name: string;
    code: string;
    permissions: string[];
  };
  scopeType?: "ORGANIZATION" | "COMPANY" | "REGION" | "LOCATION" | null;
  scopeId?: number | null;
  grantedAt: string;
  expiresAt?: string | null;
}

export interface AssignRoleScopeInput {
  scopeType: "ORGANIZATION" | "COMPANY" | "REGION" | "LOCATION";
  scopeId: number;
}

type UsersApiResponse = User[] | PaginatedResponse<User>;

function isPaginatedUsersResponse(payload: unknown): payload is PaginatedResponse<User> {
  return !!payload && typeof payload === 'object' && Array.isArray((payload as PaginatedResponse<User>).data);
}

function toUsersArray(payload: UsersApiResponse): User[] {
  return isPaginatedUsersResponse(payload) ? payload.data : payload;
}

function sortUsers(
  rows: User[],
  sortBy?: string,
  sortOrder: 'asc' | 'desc' = 'asc',
): User[] {
  if (!sortBy) {
    return rows;
  }

  const direction = sortOrder === 'desc' ? -1 : 1;

  const getSortValue = (user: User): string | number => {
    switch (sortBy) {
      case 'name':
        return `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim().toLowerCase();
      case 'email':
        return user.email.toLowerCase();
      case 'createdAt':
      case 'updatedAt':
        return Date.parse(String(user[sortBy])) || 0;
      default:
        return String((user as unknown as Record<string, unknown>)[sortBy] ?? '').toLowerCase();
    }
  };

  return [...rows].sort((a, b) => {
    const av = getSortValue(a);
    const bv = getSortValue(b);

    if (typeof av === 'number' && typeof bv === 'number') {
      return (av - bv) * direction;
    }

    return String(av).localeCompare(String(bv)) * direction;
  });
}

export const userApi = {
  getAll: async () => {
    const response = await api.get<UsersApiResponse>('/users');
    return toUsersArray(response);
  },
  paginate: async (
    params?: PaginationParams & { isActive?: boolean; roleCode?: string; adminScope?: boolean },
  ): Promise<PaginatedResponse<User>> => {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.search) query.set('search', params.search);
    if (params?.sortBy) query.set('sortBy', params.sortBy);
    if (params?.sortOrder) query.set('sortOrder', params.sortOrder);
    if (typeof params?.isActive === 'boolean') query.set('isActive', String(params.isActive));
    if (params?.roleCode) query.set('roleCode', params.roleCode);

    const endpoint = query.toString() ? `/users?${query.toString()}` : '/users';
    // adminScope sends X-Admin-Scope: true so a super admin's org context is
    // nulled server-side (AdminScopeGuard) and results span every organization.
    const response = await api.get<UsersApiResponse>(endpoint, { adminScope: params?.adminScope });

    if (isPaginatedUsersResponse(response)) {
      return response;
    }

    const users = response;

    const page = Math.max(1, params?.page ?? 1);
    const limit = Math.max(1, params?.limit ?? 10);
    const search = params?.search?.trim().toLowerCase();

    let filtered = users;

    if (typeof params?.isActive === 'boolean') {
      filtered = filtered.filter((user) => user.isActive === params.isActive);
    }

    if (search) {
      filtered = filtered.filter((user) => {
        const name = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
        const company = user.company?.name ?? '';
        const role = user.userRoles?.[0]?.role?.name ?? '';
        const haystack = `${name} ${user.email} ${company} ${role}`.toLowerCase();
        return haystack.includes(search);
      });
    }

    const sorted = sortUsers(filtered, params?.sortBy, params?.sortOrder);
    const total = sorted.length;
    const totalPages = Math.max(1, Math.ceil(total / limit));
    const safePage = Math.min(page, totalPages);
    const start = (safePage - 1) * limit;
    const data = sorted.slice(start, start + limit);

    return {
      data,
      meta: {
        total,
        page: safePage,
        limit,
        totalPages,
        hasNext: safePage < totalPages,
        hasPrev: safePage > 1,
      },
    };
  },
  getGlobalStats: async (
    options?: { adminScope?: boolean },
  ): Promise<{ total: number; active: number; inactive: number }> => {
    const stats = await api.get<DashboardUsersStats>(`/users/stats`, {
      adminScope: options?.adminScope,
    });

    return {
      total: stats.totalUsers,
      active: stats.activeUsers ?? 0,
      inactive: stats.inactiveUsers ?? 0,
    };
  },
  getStats: () => api.get<DashboardUsersStats>(`/users/stats`),
  getScopedStats: (filters: { organizationId?: number; companyId?: number }) => {
    const params = new URLSearchParams();
    if (filters?.organizationId !== undefined) params.set('organizationId', String(filters.organizationId));
    if (filters?.companyId !== undefined) params.set('companyId', String(filters.companyId));
    const query = params.toString();
    return api.get<DashboardUsersStats>(`/users/stats${query ? `?${query}` : ''}`);
  },
  getById: (id: string) => api.get<User>(`/users/${id}`),
  getRoles: (id: string) => api.get<UserRoleAssignment[]>(`/users/${id}/roles`),
  getAccessibleLocations: (id: string) => api.get<Location[]>(`/users/${id}/locations`),
  create: (data: CreateUserInput) => api.post<User>('/users', data),
  update: (id: string, data: UpdateUserInput) => api.patch<User>(`/users/${id}`, data),
  assignRole: (id: string, roleId: string, scopes?: AssignRoleScopeInput[]) =>
    api.put<User>(`/users/${id}/role`, { roleId, scopes }),
  addScope: (
    id: string,
    data: { roleId: number; scopeType: "ORGANIZATION" | "COMPANY" | "REGION" | "LOCATION"; scopeId: number },
  ) => api.post(`/users/${id}/scopes`, data),
  removeScope: (id: string, userRoleId: number) =>
    api.delete(`/users/${id}/scopes/${userRoleId}`),
  delete: (id: string) => api.delete(`/users/${id}`),
  setPassword: (id: string, password: string) =>
    api.post<{ success: boolean }>(`/users/${id}/set-password`, { password }),
};
